import React from 'react';
import _ from 'lodash';

import InvoiceRefundPanel from './InvoiceRefundPanel';
import InvoicePaymentPanel from './InvoicePaymentPanel';
import NewCarryoverLineItemPanel from './NewCarryoverLineItemPanel';
import InvoiceAdjustmentPanel from './InvoiceAdjustmentPanel';

import ProcessingIndicator from '../../Common/ProcessingIndicator';
import ActionButton from '../../Common/ActionButton';
import NoticeBlock from "../../Common/NoticeBlock";

import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import InputDateField from '../../Fields/InputDateField';
import InputSelectField from '../../Fields/InputSelectField';
import InputCurrencyField from '../../Fields/InputCurrencyField';
import TransactionTable from '../../Fields/TransactionTable';
import InvoiceLineItemTable from '../../Fields/InvoiceLineItemTable';

import Invoice, { newInvoiceAllowedTypes } from '../../../Entities/Invoice';
import InvoiceLineItem from '../../../Entities/InvoiceLineItem';
import LineItemEntry from '../../../Entities/LineItemEntry';
import Transaction from '../../../Entities/Transaction';

import InvoiceValidator from "../../../Validators/Invoice";

import {
  queryCreateInvoice,
  queryUpdateInvoice
} from '../../../Queries';

import {
  convertUnixMsToDateInTimezone,
  getCurrentDateInTimezone,
  formatDateForDisplay,
} from '../../../Utils/date';

import { InvoiceContext } from '../../../Context/InvoiceContext';
import featureFlags from '../../../featureFlags';

import './InvoicePanel.scss';

const oldInvoiceQueryFields = [
  'id',
  'readable_id',
  'subscription_id',
  'due_on',
  'balance',
  'parking_period_month',
  'parking_period_year',
  'notes',
  'detail_notes',
  'type',
  {
    'line_items': [
      'id',
      'balance',
      'adjusted_gross_amount',
      'adjusted_gross_amounts',
      featureFlags.CARRYOVER_RATE ? 'line_item_rate_type' : '',
      {
        'entries': [
          'id',
          'line_item_id',
          'type',
          'amount',
          'amount_tax_1',
          'amount_tax_2',
          'amount_tax_3',
          'amount_tax_4',
          'gross_amount',
          'created_at',
          {
            'transaction': [
              'id',
              'notes',
              'invoice_id',
              'line_item_entry_id',
              'created_at',
              'application_date'
            ]
          },
          {
            'line_item_payment': [
              'reversal_entry_id',
              {
                'payment': [
                  'id',
                  'type',
                  'payment_meta',
                  'amount'
                ]
              }
            ]
          }
        ]
      },
      {
        'parker': [
          'id',
          'first_name',
          'last_name',
          'selected_start_date',
          'selected_end_date'
        ]
      },
      {
        'rate': [
          'id',
          'name'
        ]
      },
      {
        'fee': [
          'type',
          'price',
          'tax_1',
          'tax_2',
          'tax_3',
          'tax_4'
        ]
      },
      {
        'validation': [
          'quantity',
          'type',
          'product_name'
        ]
      }
    ]
  },
  {
    'subscription': [
      {
        'payor': [
          'readable_id',
          'billing_firstname',
          'billing_lastname',
          'info_name'
        ]
      },
      {
        'billing_profile': [
          'facility_id'
        ]
      },
    ]
  },
  {
    'payments': [
      'id',
      'type',
      'payment_meta',
      'amount'
    ]
  },
  {
    'refunds': [
      'id',
      'payment_id',
      'type',
      'refund_meta',
      'amount',
    ]
  },
  {
    'transaction': [
      'id',
      'notes',
      'invoice_id',
      'line_item_entry_id',
      'created_at'
    ]
  },
  'created_at'
];

export const invoiceQueryFields = featureFlags.OVERPAYMENT ? [
  'id',
  'readable_id',
  'subscription_id',
  'due_on',
  'balance',
  'parking_period_month',
  'parking_period_year',
  'notes',
  'detail_notes',
  'type',
  {
    'line_items': [
      'id',
      'balance',
      'adjusted_gross_amount',
      'adjusted_gross_amounts',
      featureFlags.CARRYOVER_RATE ? 'line_item_rate_type' : '',
      {
        'entries': [
          'id',
          'line_item_id',
          'type',
          'amount',
          'amount_tax_1',
          'amount_tax_2',
          'amount_tax_3',
          'amount_tax_4',
          'gross_amount',
          'created_at',
          {
            'transaction': [
              'id',
              'notes',
              'invoice_id',
              'line_item_entry_id',
              'created_at',
              'application_date'
            ]
          },
          {
            'line_item_payment': [
              'reversal_entry_id',
              {
                'payment': [
                  'id',
                  'type',
                  'payment_meta',
                  'amount',
                  'credit_entry_id',
                  {
                    'credit_entry': [
                      'application_date',
                      {
                        'credit': [
                          'application_type',
                          {
                            'credit_entries': [
                              'type',
                              'application_date',
                            ]
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            'line_item_reversal': [
              {
                'payment': [
                  'id',
                  'type',
                  'payment_meta',
                  'amount',
                  'credit_entry_id',
                  {
                    'credit_entry': [
                      'application_date',
                      {
                        'credit': [
                          'application_type',
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        'parker': [
          'id',
          'first_name',
          'last_name',
          'selected_start_date',
          'selected_end_date'
        ]
      },
      {
        'rate': [
          'id',
          'name'
        ]
      },
      {
        'fee': [
          'type',
          'price',
          'tax_1',
          'tax_2',
          'tax_3',
          'tax_4'
        ]
      },
      {
        'validation': [
          'quantity',
          'type',
          'product_name'
        ]
      }
    ]
  },
  {
    'subscription': [
      {
        'payor': [
          'readable_id',
          'billing_firstname',
          'billing_lastname',
          'info_name'
        ]
      },
      {
        'billing_profile': [
          'facility_id'
        ]
      },
      {
        'credit': [
          'balance',
        ]
      },
    ]
  },
  {
    'payments': [
      'id',
      'type',
      'payment_meta',
      'amount',
      'credit_entry_id',
      {
        'credit_entry': [
          'application_date',
          {
            'credit': [
              'application_type',
              {
                'credit_entries': [
                  'type',
                  'application_date'
                ]
              }
            ]
          }
        ]
      },
      {
        'credit': [
          'amount',
          {
            'credit_entries': [
              'type',
              'application_date',
            ]
          }
        ]
      }
    ]
  },
  {
    'refunds': [
      'id',
      'payment_id',
      'type',
      'refund_meta',
      'amount',
    ]
  },
  {
    'transaction': [
      'id',
      'notes',
      'invoice_id',
      'line_item_entry_id',
      'created_at'
    ]
  },
  'created_at'
] : oldInvoiceQueryFields;

const resetLineItemEntryAmounts = (invoice) => {
  const fieldsToReset = [
    'amount',
    'gross_amount',
    'amount_tax_1',
    'amount_tax_2',
    'amount_tax_3',
    'amount_tax_4'
  ]

  const invoiceCopy = _.cloneDeep(invoice);
  
  invoiceCopy.fields.line_items.value.forEach(item => {
    item.fields.entries.value.forEach(entry => {
      fieldsToReset.forEach(field => {
        entry.fields[field].value = null;
        entry.fields[field].valid = true;
      })
    })
  })

  return invoiceCopy
}

class InvoicePanel extends React.Component {
  state = {
    invoice: null,
    invoiceSaveInProgress: false,
    invoiceLineItemsEditState: false,
    invoiceLineItemsSaveInProgress: false,
    invoiceNotesEditState: false,
    invoiceNotesSaveInProgress: false,
    invoiceDetailNotesEditState: false,
    invoiceDetailNotesSaveInProgress: false,
    invoiceDueDateEditState: false,
    invoiceDueDateSaveInProgress: false,
    invoiceEditState: false,
    paymentEditState: false,
    refundEditState: false,
    transactionEditState: false,
    hasChanged: false,
    creditReversal: {
      credit_reversed: false,
      overpayment_credit: null
    },
  };

  editStateStoredValue = {
    due_on: null,
    notes: null,
    line_items: null
  };

  get fieldConfiguration() {
    return this.props.config.fieldConfiguration;
  }

  get subscriptions() {
    return this.props.config.subscriptions;
  }

  get rates() {
    return this.props.config.rates;
  }
  get fees() {
    return this.props.config.fees;
  }

  get isValid() {
    return InvoiceValidator.isFormValid(this.state.invoice);
  }

  subscriptionId;

  constructor() {
    super(...arguments);
    const {
      config
    } = this.props;

    this.state = {
      ...this.state,
      ...config.initialState,
      lineItemFeeTypes: [],
      selectedFees: [],
    } 
    this.updateSelectedFees = this.updateSelectedFees.bind(this);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.subscriptionId = urlSearchParams.get('subscriptionId');
  }


  componentDidMount() {    
    if(featureFlags.PAYOR_SEARCH && this.subscriptionId) {
      const invoice = _.cloneDeep(this.state.invoice)
      invoice.fields['subscription_id'].value = this.subscriptionId;
      this.setState({ invoice })
    }
  }

  // Reset line item amounts when subscription changes
  // This prevents values from getting locked into disabled fields
  componentDidUpdate(_prevProps, prevState) {
    const { invoice: prevInvoice } = prevState;
    const { invoice } = this.state;

    const prevSubscription = prevInvoice?.fields.subscription_id.value;
    const currentSubscription = invoice?.fields.subscription_id.value;

    const findCurrentSubscription = this.subscriptions.find((sub) => sub.fields.id.value === currentSubscription)
    const isTaxExempt = findCurrentSubscription?.fields.tax_exempt.value;

    if(currentSubscription && isTaxExempt && prevSubscription !== currentSubscription) {
      const resetLineItemEntries = resetLineItemEntryAmounts(invoice);
      this.setState({ invoice: resetLineItemEntries })
    }
  }

  getLineItemsToDisplay(lineItems) {
    const typesToHide = ['Activation', 'Access Device', 'Lost Device', 'Credit Card', 'Nonrecurring Payment', 'Mail Invoice', 'Late'];
    const lineItemsToDisplay = lineItems.filter(lineItem => {
      const lineItemFee = lineItem.fields.fee.value;
      const lineItemAmount = lineItem.fields.adjusted_gross_amount.value;

      if (typesToHide.includes(lineItemFee?.fields.type.value) && lineItemAmount === 0) {
        const feeType = lineItemFee.fields.type.value;

        if (feeType === 'Credit Card') {
          return false;
        }
        else {
          const originalCharge = lineItem.fields.entries.value.find(entry => entry.fields.type.value === 'CHARGE');
          const originalPrice = originalCharge.fields.gross_amount.value;

          return originalPrice !== 0;
        }
      }

      return true;
    });

    return lineItemsToDisplay;
  }

  getTransactionTableRows(invoice) {
    invoice = invoice.fields;

    const invoice_transaction = this.createTransaction(invoice, true);

    const transactions = invoice.line_items.value.reduce((transaction_list, line_item) => {
      const lineItemEntries = line_item.fields.entries.value;
      const validTypes = Object.keys(Transaction.getTypeEnumForHistoryReports());
      const entries = lineItemEntries.filter(entry => validTypes.includes(entry.fields.type.value));

      return [...transaction_list, ...entries.reduce((entry_transactions, entry) => {
        entry = entry.fields;
        const entry_transaction = this.createTransaction(entry, false);
        return entry_transaction ? [...entry_transactions, entry_transaction] : entry_transactions;
      }, [])];
    }, invoice_transaction ? [invoice_transaction] : []);

    return transactions;
  }

  createTransaction(item, isInvoice) {
    const { facilityTimezone } = this.fieldConfiguration;
    const transactionTypes = Transaction.getTypeEnum();

    const transaction = item.transaction?.value;
    if (!transaction) return null;

    return {
      id: transaction.fields.id.value,
      date: convertUnixMsToDateInTimezone(transaction.fields.created_at.value, facilityTimezone),
      application_date: this.getApplicationDate(item.type.value, transaction.fields.application_date.value),
      type: transactionTypes[item.type.value] || item.type.value,
      notes: transaction.fields.notes.value,
      invoice_id: isInvoice ? item.id.value : null,
      line_item_id: isInvoice ? null : item.line_item_id.value,
      line_item_entry_id: isInvoice ? null : item.id.value,
      line_item_payment: item.line_item_payment ?? null
    };
  }

  getApplicationDate(type, date) {
    switch (type) {
      case 'AUTO_INVOICE':
      case 'MANUAL_INVOICE':
      case 'FIRST_MONTH_INVOICE':
      case 'ADJUSTMENT':
      case 'VALIDATION_INVOICE':
        return '';
      default:
        return date ? formatDateForDisplay(date) : '';
    }
  }

  async createInvoice(invoice) {
    const data = invoice.getCreateQueryFieldValues();
    data.selectedFeeHistoryIds = this.state.selectedFees.map((sf) => (sf.feesHistory[0].id));
    try {
      const response = await queryCreateInvoice(data, invoiceQueryFields);
      if (response.errors) {
        throw response.errors.map(error => error.message);
      }

      const invoiceId = response.data.createInvoice.id;
      return invoiceId;
    } catch (error) {
      throw error;
    }
  }


  saveInvoice(invoice) {
    return new Promise((resolve, reject) => {
      const data = invoice.getUpdateQueryFieldValues();

      queryUpdateInvoice(data, invoiceQueryFields).then(response => {
        if (response.errors) {
          reject(response.errors.map(error => error.message));
          return;
        }

        const invoice = Invoice.createFromQueryValues(response.data.updateInvoice);
        resolve(invoice);
      }, error => {
        reject([error]);
      });
    });
  }

  enterInvoiceEditState(field) {
    const stateChanges = {};

    switch (field) {
      case 'due_on':
        stateChanges.invoiceDueDateEditState = true;
        this.editStateStoredValue.due_on = this.state.invoice.fields.due_on.value;
        break;
      case 'notes':
        stateChanges.invoiceNotesEditState = true;
        this.editStateStoredValue.notes = this.state.invoice.fields.notes.value;
        break;
      case 'detail_notes':
        stateChanges.invoiceDetailNotesEditState = true;
        this.editStateStoredValue.detail_notes = this.state.invoice.fields.detail_notes?.value;
        break;
      case 'line_items':
        stateChanges.invoiceNotesEditState = true;
        stateChanges.invoiceLineItemsEditState = true;
        this.editStateStoredValue.notes = this.state.invoice.fields.notes.value;
        break;
      default:
    }

    this.setState(stateChanges);
  }


  exitInvoiceEditState(field, saveChanges) {
    if (saveChanges) {
      const stateChanges = {};

      switch (field) {
        case 'due_on':
          stateChanges.invoiceDueDateSaveInProgress = true;
          break;
        case 'notes':
          stateChanges.invoiceNotesSaveInProgress = true;
          break;
        case 'detail_notes':
          stateChanges.invoiceDetailNotesSaveInProgress = true;
          break;
        case 'line_items':
          stateChanges.invoiceLineItemsSaveInProgress = true;
          stateChanges.invoiceNotesSaveInProgress = true;
          break;
        default:
      }

      this.setState(stateChanges, () => {
        this.saveInvoice(this.state.invoice).then(invoice => {
          const stateChanges = { invoice };

          switch (field) {
            case 'due_on':
              stateChanges.invoiceDueDateSaveInProgress = false;
              stateChanges.invoiceDueDateEditState = false;
              this.editStateStoredValue.due_on = null;
              break;
            case 'notes':
              stateChanges.invoiceNotesSaveInProgress = false;
              stateChanges.invoiceNotesEditState = false;
              this.editStateStoredValue.notes = null;
              break;
            case 'detail_notes':
              stateChanges.invoiceDetailNotesSaveInProgress = false;
              stateChanges.invoiceDetailNotesEditState = false;
              this.editStateStoredValue.detail_notes = null;
              break;
            case 'line_items':
              stateChanges.invoiceLineItemsSaveInProgress = false;
              stateChanges.invoiceLineItemsEditState = false;
              stateChanges.invoiceNotesSaveInProgress = false;
              stateChanges.invoiceNotesEditState = false;
              stateChanges.invoiceEditState = false
              this.editStateStoredValue.line_items = null;
              this.editStateStoredValue.notes = null;
              break;
            default:
          }

          this.setState(stateChanges);
        }, errors => {
          let errorMessage = 'An error occurred while saving changes to the invoice:\n\n';
          for (let error of errors) {
            errorMessage += error + '\n';
          }
          alert(errorMessage);

          const stateChanges = {};

          switch (field) {
            case 'due_on':
              stateChanges.invoiceDueDateSaveInProgress = false;
              break;
            case 'notes':
              stateChanges.invoiceNotesSaveInProgress = false;
              break;
            case 'detail_notes':
              stateChanges.invoiceDetailNotesSaveInProgress = false;
              break;
            case 'line_items':
              stateChanges.invoiceLineItemsSaveInProgress = false;
              stateChanges.invoiceNotesSaveInProgress = false;
              break;
            default:
          }

          this.setState(stateChanges);
        });
      });
    }
    else {
      const stateChanges = {};

      const invoice = _.cloneDeep(this.state.invoice);

      switch (field) {
        case 'due_on':
          stateChanges.invoiceDueDateEditState = false;
          invoice.fields.due_on.value = this.editStateStoredValue.due_on;
          this.editStateStoredValue.due_on = null;
          break;
        case 'notes':
          stateChanges.invoiceNotesEditState = false;
          invoice.fields.notes.value = this.editStateStoredValue.notes;
          this.editStateStoredValue.notes = null;
          break;
        case 'detail_notes':
          stateChanges.invoiceDetailNotesEditState = false;
          invoice.fields.detail_notes.value = this.editStateStoredValue.detail_notes;
          this.editStateStoredValue.detail_notes = null;
          break;
        case 'line_items':
          stateChanges.invoiceLineItemsEditState = false;
          stateChanges.invoiceNotesEditState = false;
          stateChanges.invoiceEditState = false;
          invoice.fields.line_items.value = this.editStateStoredValue.line_items;
          invoice.fields.notes.value = this.editStateStoredValue.notes;
          this.editStateStoredValue.line_items = null;
          this.editStateStoredValue.notes = null;
          break;
        default:
      }

      stateChanges.invoice = invoice;

      this.setState(stateChanges);
    }
  }


  enterPaymentEditState() {
    this.setState({ paymentEditState: true });
  }


  exitPaymentEditState = invoice => {
    const stateChanges = {
      paymentEditState: false
    };
    if (invoice) {
      stateChanges.invoice = invoice;
    }

    this.setState(stateChanges);
  }

  enterRefundEditState() {
    this.setState({ refundEditState: true });
  }

  exitRefundEditState = ({ invoice, overpayment_credit, credit_reversed }) => {
    this.setState({
      refundEditState: false,
      creditReversal: {
        overpayment_credit,
        credit_reversed,
      },
      ...(invoice ? { invoice } : {})
    });
  }

  _storeInvoiceData(inputId) {
    return value => {
      const invoice = _.cloneDeep(this.state.invoice);
      switch (inputId) {
        case 'type':
          const lineItem = new InvoiceLineItem();
          const lineItemEntry = new LineItemEntry();

          const lineItemRateType = value === 'CARRYOVER_INVOICE' ? 'MONTHLY' : null;
          lineItem.fields.line_item_rate_type.value = lineItemRateType;
          lineItemEntry.fields.type.value = 'CHARGE';

          lineItem.fields.entries.value = [lineItemEntry];
          invoice.fields.line_items.value = [lineItem];

          this.setState({ lineItemFeeTypes: [] })
          break;
        case 'subscription_id':
          const invoiceLineItemsWithNonNullParkerValues = this.state.invoice.fields.line_items.value.filter(lineItem => {
            return (
              lineItem.fields.parker.value !== null &&
              lineItem.fields.parker.value.fields.id.value !== null
            );
          });
          if (invoiceLineItemsWithNonNullParkerValues.length) {
            window.alert('Payor cannot be changed if a Parker has been selected for any line item.');
            return;
          }

          break;
        default:
      }
      invoice.fields[inputId].value = value;

      this.setState({ invoice });
    };
  }

  _deleteLineItem(value) {
    const invoice = _.cloneDeep(this.state.invoice);

    invoice.fields.line_items.value = value

    this.setState({ invoice });
  }

  updateSelectedFees(index, invoiceLineIndex) {
    const selectedFeeCopy = [...this.state.selectedFees];
    const selectedFee = this.fees[index];
    selectedFeeCopy[invoiceLineIndex] = selectedFee;
    const invoiceLineItemsCopy = _.cloneDeep(this.getLineItemsToDisplay(this.state.invoice.fields.line_items.value));
    invoiceLineItemsCopy[invoiceLineIndex].fields.fee.value = {
      fields: {
        id: {
          value: selectedFee.id,
        },
        type: {
          value: selectedFee.feeType.name.toUpperCase().replace(/ /g,"_")
        },
        price: {
          value: Math.trunc(selectedFee.feesHistory[0].price)
        },
        tax_1: {
          value: selectedFee.feesHistory[0].tax1 ? Math.trunc(selectedFee.feesHistory[0].tax1) : 0,
        },
        tax_2: {
          value: selectedFee.feesHistory[0].tax2 ? Math.trunc(selectedFee.feesHistory[0].tax2) : 0,
        },
        tax_3: {
          value: selectedFee.feesHistory[0].tax3 ? Math.trunc(selectedFee.feesHistory[0].tax3) : 0,
        },
        tax_4: {
          value: selectedFee.feesHistory[0].tax4 ? Math.trunc(selectedFee.feesHistory[0].tax4) : 0,
        },
        name: {
          value: selectedFee.feeName,
        }
      }
    }
    const lineItemEntry = new LineItemEntry();
    lineItemEntry.fields = {
        amount: {
          value: selectedFee.feesHistory[0].price,
          valid: true,
        },
        amount_tax_1: {
          value: selectedFee.feesHistory[0].tax1 ? selectedFee.feesHistory[0].tax1: 0,
          valid: true,
        },
        amount_tax_2: {
          value: selectedFee.feesHistory[0].tax2 ? selectedFee.feesHistory[0].tax2: 0,
          valid: true,
        },
        amount_tax_3: {
          value: selectedFee.feesHistory[0].tax3 ? selectedFee.feesHistory[0].tax3: 0,
          valid: true,
        },
        amount_tax_4: {
          value: selectedFee.feesHistory[0].tax4 ? selectedFee.feesHistory[0].tax4: 0,
          valid: true,
        },
        created_at: {value: null},
        gross_amount: {value: (selectedFee.feesHistory[0].price + selectedFee.feesHistory[0].tax1 + selectedFee.feesHistory[0].tax2 + selectedFee.feesHistory[0].tax3 + selectedFee.feesHistory[0].tax4).toFixed(2), valid: true},
        id: {value: null},
        line_item_id: {value: null},
        line_item_payment: {value: null},
        line_item_reversal: {value: null},
        payment_id: {value: null},
        refund_id: {value: null},
        transaction: {value: null},
        type: {value: 'CHARGE'}
    }
    invoiceLineItemsCopy[invoiceLineIndex].fields.entries.value = [lineItemEntry]
    const line_Item = invoiceLineItemsCopy.map((lineItem, index) => {
      const id = lineItem.fields.fee.value?.fields.id.value
      return {id: id, showFeeTypeError: false}
    })
    this._storeInvoiceData('line_items')(invoiceLineItemsCopy);
    this.setState({ lineItemFeeTypes: line_Item, hasChanged: true,  selectedFees: [...selectedFeeCopy] });
  }


  onInvoiceLineItemChange = (ident, value) => {
    const lineItems = _.cloneDeep(this.getLineItemsToDisplay(this.state.invoice.fields.line_items.value));

    if (ident === null) ident = lineItems.length;

    if (value === null) {
      lineItems.splice(ident, 1);
    }
    else {
      lineItems[ident] = value;
    }
    if(this.state.invoice.fields.type.value === 'MANUAL_INVOICE') {
    const line_Item = lineItems.map((lineItem, index) => {
      const id = lineItem.fields.fee.value?.fields.id.value
      return {id: id, showFeeTypeError: lineItem.fields.parker.value?.fields.id.value ? id ? false : this.state.lineItemFeeTypes?.[index]?.showFeeTypeError : false}})
    this.setState({ lineItemFeeTypes: line_Item });
    }
    this.setState({ hasChanged: true });
    this._storeInvoiceData('line_items')(lineItems);
  }

  OnInvoiceLineItemDelete = inputId => {
    this._deleteLineItem(inputId)
  }

  handleInvoiceDueDateEditClick = () => {
    this.enterInvoiceEditState('due_on');
  }
  handleInvoiceDueDateSaveClick = () => {
    this.exitInvoiceEditState('due_on', true);
  }
  handleInvoiceDueDateCancelClick = () => {
    this.exitInvoiceEditState('due_on', false);
  }


  handleInvoiceNotesEditClick = () => {
    this.enterInvoiceEditState('notes');
  }
  handleInvoiceNotesSaveClick = () => {
    this.exitInvoiceEditState('notes', true);
  }
  handleInvoiceNotesCancelClick = () => {
    this.exitInvoiceEditState('notes', false);
  }
  handleInvoiceDetailNotesEditClick = () => {
    this.enterInvoiceEditState('detail_notes');
  }
  handleInvoiceDetailNotesSaveClick = () => {
    this.exitInvoiceEditState('detail_notes', true);
  }
  handleInvoiceDetailNotesCancelClick = () => {
    this.exitInvoiceEditState('detail_notes', false);
  }

  handleTransactionNotesEditClick = () => {
    return new Promise((resolve) => {
      this.setState({ transactionEditState: true }, () => {
        resolve();
      });
    });
  }

  handleTransactionNotesCancelClick = () => {
    this.setState({ transactionEditState: false });
  }

  handleAdjustInvoiceActionClick = () => {
    this.enterInvoiceEditState('line_items');
  }
  handleAdjustInvoiceSaveClick = () => {
    this.exitInvoiceEditState('line_items', true);
  }
  handleAdjustInvoiceCancelClick = () => {
    this.exitInvoiceEditState('line_items', false);
  }


  handleApplyPaymentClick = () => {
    this.enterPaymentEditState();
  }

  handleApplyRefundClick = () => {
    this.enterRefundEditState();
  }

  handleSave = async () => {
    try {
      const invoiceId = await this.createInvoice(this.state.invoice);
      window.location.href += `&invoice=${invoiceId}`;
    } catch (errors) {
      let errorMessage = 'An error occurred while creating the invoice:\n\n';
      for (let error of errors) {
        errorMessage += error + '\n';
      }

      throw errorMessage;
    }
  }

  handleSaveInvoice = () => {
    this.setState({
      invoiceSaveInProgress: true
    }, async () => {
      try {
        if (featureFlags.CARRYOVER_RATE) {
          if (this.state.invoice.fields.type.value === 'CARRYOVER_INVOICE') {
            InvoiceValidator.carryOverInvoiceCheck(this.state.invoice.fields.line_items.value);
          }
        }
        await this.handleSave();
      } catch (error) {
        if (featureFlags.CARRYOVER_RATE) {
          alert(error);
        }
        this.setState({ invoiceSaveInProgress: false });
      }
    });
  }

  handleSaveInvoiceClick = () => {
      if(this.state.invoice.fields.type.value === 'MANUAL_INVOICE') {
        const line_Item = this.state.invoice.fields.line_items.value.map(lineItem => {
          const type = lineItem.fields.fee.value?.fields.type.value;
          return {id: lineItem.fields.fee.value?.fields.id.value, showFeeTypeError: type ? false : true }})
        this.setState({ lineItemFeeTypes: line_Item })
        const items = line_Item.filter(item => item.showFeeTypeError)
        if(items.length > 0) {
          return false
        }
      }
    this.handleSaveInvoice()
  }

  getInvoiceDate() {
    const { invoice } = this.state;
    const { newMode } = this.props;
    const { facilityTimezone } = this.fieldConfiguration;

    return newMode ?
      new Date(getCurrentDateInTimezone(facilityTimezone)) :
      new Date(convertUnixMsToDateInTimezone(invoice.fields.created_at.value, facilityTimezone));
  }

  renderInvoiceAttributes() {
    const {
      invoice,
      invoiceDueDateEditState,
      invoiceDueDateSaveInProgress,
      invoiceNotesEditState,
      invoiceLineItemsEditState,
      paymentEditState,
      refundEditState,
      transactionEditState
    } = this.state;

    const {
      newMode = false
    } = this.props;

    const balance = newMode || invoiceLineItemsEditState ? invoice.currentBalance : invoice.fields.balance.value;

    const typeOptions = newMode ? this.fieldConfiguration.invoice.type.options.filter(option => {
      return newInvoiceAllowedTypes.includes(option.value);
    }) : this.fieldConfiguration.invoice.type.options;

    return (
      <div className="parkchirp--invoice-panel--invoice-attributes-container">
        <div className="parkchirp--invoice-panel--invoice-attributes-block">
          <div className="parkchirp--invoice-panel--invoice-attribute">
            <Field label="Invoice Type"
              id="parkchirp--invoice--type"
              layout="columns"
            >
              <InputSelectField inputId="parkchirp--invoice--type"
                options={typeOptions}
                value={invoice.fields.type.value}
                onChange={this._storeInvoiceData('type')}
                editMode={newMode}
              />
            </Field>
          </div>
          {!featureFlags.SUBSCRIPTION_NAME &&
            <div className="parkchirp--invoice-panel--invoice-attribute">
              <Field label="Primary Contact"
                id="parkchirp--invoice--subscription-id"
                layout="columns">
                <InputSelectField inputId="parkchirp--invoice--subscription-id"
                  options={this.fieldConfiguration.invoice.subscription_id.options}
                  value={invoice.fields.subscription_id.value}
                  onChange={this._storeInvoiceData('subscription_id')}
                  editMode={newMode}
                />
              </Field>
            </div>
          }
          {!invoice.isCarryover && <div className="parkchirp--invoice-panel--invoice-attribute parkchirp--invoice-panel--invoice-attribute--parking-period">
            <Field label="Parking Period"
              id="parkchirp--invoice--parking-period"
              layout="columns">
              <InputSelectField inputId="parkchirp--invoice--parking-period-month"
                options={this.fieldConfiguration.invoice.parking_period_month.options}
                value={invoice.fields.parking_period_month.value}
                onChange={this._storeInvoiceData('parking_period_month')}
                editMode={newMode}
              />
              <span>&nbsp;</span>
              <InputSelectField inputId="parkchirp--invoice--parking-period-year"
                options={this.fieldConfiguration.invoice.parking_period_year.options}
                value={invoice.fields.parking_period_year.value}
                onChange={this._storeInvoiceData('parking_period_year')}
                editMode={newMode}
              />
            </Field>
          </div>}
        </div>
        <div className="parkchirp--invoice-panel--invoice-attributes-block">
          <div className="parkchirp--invoice-panel--invoice-attribute">
            <Field label="Invoice Number"
              id="parkchirp--invoice--readable-id"
              containerClasses={['parkchirp--invoice-panel--field-container--readable-id']}
              layout="columns">
              <InputTextField inputId="parkchirp--invoice--readable-id"
                value={(newMode) ? 'Auto-generated when invoice is saved.' : invoice.fields.readable_id.value}
                editMode={false}
              />
            </Field>
          </div>
          <div className="parkchirp--invoice-panel--invoice-attribute">
            <Field label="Invoice Date"
              id="parkchirp--invoice--created-at"
              containerClasses={['parkchirp--invoice-panel--field-container--created-at']}
              layout="columns">
              <InputDateField inputId="parkchirp--invoice--created-at"
                value={this.getInvoiceDate()}
                editMode={false}
              />
            </Field>
          </div>
        </div>
        <div className="parkchirp--invoice-panel--invoice-attributes-block">
          {featureFlags.ACCOUNT_ID &&
            <>
              {!newMode &&
                <div className="parkchirp--invoice-panel--invoice-attribute">
                  <Field label="Account ID"
                    id="parkchirp--invoice--account-id"
                    layout="columns">
                    <InputTextField inputId="parkchirp--invoice--account-id"
                      value={this.fieldConfiguration.invoice.accountId}
                      editMode={false}
                    />
                  </Field>
                </div>
              }
            </>
          }
          {featureFlags.SUBSCRIPTION_NAME &&
            <>
              {!newMode &&
                <div className="parkchirp--invoice-panel--invoice-attribute">
                  <Field label="Subscription Name"
                    id="parkchirp--invoice--subscription-name"
                    layout="columns">
                    <InputTextField inputId="parkchirp--invoice--subscription-name"
                      value={this.fieldConfiguration.invoice.subscriptionName}
                      editMode={false}
                    />
                  </Field>
                </div>
              }
            </>
          }
        </div>
        <div className="parkchirp--invoice-panel--invoice-attributes-block">
          <div className="parkchirp--invoice-panel--invoice-attribute">
            <Field label="Subscription"
              id="parkchirp--invoice--subscription-id"
              layout="columns">
              <InputSelectField inputId="parkchirp--invoice--subscription-id"
                options={this.fieldConfiguration.invoice.subscription_id.options}
                value={invoice.fields.subscription_id.value}
                onChange={this._storeInvoiceData('subscription_id')}
                editMode={(featureFlags.PAYOR_SEARCH && this.subscriptionId) ? false : newMode}
              />
            </Field>
          </div>
          <div className="parkchirp--invoice-panel--invoice-attribute">
            <Field label="Due Date"
              id="parkchirp--invoice--due-on"
              containerClasses={['parkchirp--invoice-panel--field-container--due-on']}
              layout="columns">
              <InputDateField inputId="parkchirp--invoice--due-on"
                key={invoice.fields.due_on.value}
                value={invoice.fields.due_on.value}
                onChange={this._storeInvoiceData('due_on')}
                editMode={newMode || invoiceDueDateEditState}
              />
              {(!newMode) &&
                <div className="parkchirp--invoice-panel--action-container--due-on">
                  {(invoiceDueDateSaveInProgress) &&
                    <ProcessingIndicator />
                  }
                  {(!invoiceNotesEditState && !invoiceLineItemsEditState && !paymentEditState && !invoiceDueDateEditState && !refundEditState && !transactionEditState) &&
                    <button type="button"
                      className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                      onClick={this.handleInvoiceDueDateEditClick}>
                      Edit
                    </button>
                  }
                  {(invoiceDueDateEditState && !invoiceDueDateSaveInProgress) &&
                    <React.Fragment>
                      <button type="button"
                        className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                        onClick={this.handleInvoiceDueDateSaveClick}>
                        Save
                      </button>
                      <button type="button"
                        className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                        onClick={this.handleInvoiceDueDateCancelClick}>
                        Cancel
                      </button>
                    </React.Fragment>
                  }
                </div>
              }
            </Field>
          </div>
        </div>

        <div className="parkchirp--invoice-panel--balances-container">
          <div className="parkchirp--invoice-panel--invoice-attributes-block">
            <div className="parkchirp--invoice-panel--invoice-attribute">
              <Field label="Total Balance"
                id="parkchirp--invoice--total-balance"
                containerClasses={['parkchirp--invoice-panel--field-container--total-balance']}
                layout="columns"
              >
                <InputCurrencyField
                  inputId="parkchirp--invoice--total-balance"
                  value={balance}
                  editMode={false}
                />
              </Field>
            </div>
          </div>
        </div>
      </div >
    );
  }

  renderAdjustInvoicePanel() {
    const lineItemsToDisplay = this.getLineItemsToDisplay(this.state.invoice.fields.line_items.value);
    const lineItemTableRows = lineItemsToDisplay.map((lineItem, index) => {
      return { ident: index, data: lineItem };
    });
    return (
      <InvoiceAdjustmentPanel 
        lineItemTableRows={lineItemTableRows}
        balance={this.state.invoice.fields.balance.value}
        currentBalance={this.state.invoice.currentBalance}
        invoiceLineItemsSaveInProgress={this.state.invoiceLineItemsSaveInProgress}
        handleAdjustInvoiceSaveClick={this.handleAdjustInvoiceSaveClick}
        handleAdjustInvoiceCancelClick={this.handleAdjustInvoiceCancelClick}
        onAdjustmentActionChange={(changeState) => {
          changeState ? this.handleAdjustInvoiceActionClick() : this.setState({ invoiceLineItemsEditState: false })
        }}
        adjustmentActionTypes={this.fieldConfiguration.invoice.adjustmentType.options}
        adjustmentTypes={this.fieldConfiguration.lineItem.type.options}
        onLineItemChange={this._storeInvoiceData('line_items')}
        lineItems={this.editStateStoredValue.line_items}
      />
    )
  }

  renderInvoiceLineItems() {
    const {
      invoice,
      invoiceLineItemsEditState,
      lineItemFeeTypes
    } = this.state;

    const {
      newMode
    } = this.props;

    const subscription = this.subscriptions.find(subscription => {
      return subscription.fields.id.value === invoice.fields.subscription_id.value;
    });

    const lineItemsToDisplay = this.getLineItemsToDisplay(invoice.fields.line_items.value);

    const lineItemTableRows = lineItemsToDisplay.map((lineItem, index) => {
      return { ident: index, data: lineItem };
    });


    return (
      <div className={`parkchirp--invoice-panel--invoice-line-items-container parkchirp--invoice-type--${invoice.fields.type.value}`}>
        <Field layout="stacked">
          <InvoiceLineItemTable inputId="parkchirp--invoice--line-items"
            lineItemRows={lineItemTableRows}
            subscription={subscription}
            rates={this.rates}
            fees={this.fees}
            selectedFees={this.state.selectedFees}
            updateSelectedFees={this.updateSelectedFees}
            onLineItemChange={this.onInvoiceLineItemChange}
            newMode={newMode}
            adjustmentMode={invoiceLineItemsEditState}
            fieldConfiguration={this.fieldConfiguration}
            allowMetadataEditing={!invoice.isCarryover}
            lineItemFeeTypes={lineItemFeeTypes}
          />
        </Field>
      </div>
    );
  }

  renderCarryoverLineItem() {
    const { invoice } = this.state;
    const { newMode } = this.props
    const lineItems = featureFlags.CARRYOVER_RATE ? invoice.fields.line_items.value : invoice.fields.line_items.value[0];

    const subscription = this.subscriptions.find(subscription => {
      return subscription.fields.id.value === invoice.fields.subscription_id.value;
    });

    return (
      <NewCarryoverLineItemPanel
        inputId="parkchirp--carryover--entry"
        lineItems={lineItems}
        lineItemConfig={this.fieldConfiguration.lineItem}
        onLineItemChange={this.onInvoiceLineItemChange}
        hasChanged={this.state.hasChanged}
        onLineItemDelete={this.OnInvoiceLineItemDelete}
        newMode={newMode}
        taxExempt={subscription?.fields.tax_exempt.value}
      />
    );
  }



  renderInvoiceActions() {
    const {
      invoice,
      invoiceSaveInProgress,
      invoiceLineItemsEditState,
      paymentEditState,
      refundEditState,
      transactionEditState,
      creditReversal,
      invoiceEditState
    } = this.state;

    const {
      newMode
    } = this.props;
    let transactions = [];
    if (refundEditState) {
      transactions = this.getTransactionTableRows(invoice);
    }
    return (
      <React.Fragment>
        {(newMode) &&
          <div className="parkchirp--invoice-panel--invoice-actions-container">
            <ActionButton label="Save Invoice"
              classNames={['parkchirp--action', 'parkchirp--action--save']}
              inProgressState={invoiceSaveInProgress}
              onClick={this.handleSaveInvoiceClick}
              disabled={!this.state.hasChanged || !this.isValid}
            />
            {featureFlags.CARRYOVER_RATE &&
              <a href={`?page=parkchirp_invoices&facility=${this.props.config?.facilityId}`} className="button button-primary button--cancel">Cancel</a>
            }
          </div>
        }
        {(!newMode && !invoiceLineItemsEditState && !paymentEditState && !refundEditState && !transactionEditState && !invoiceEditState) &&
          <>
            <div className="parkchirp--invoice-panel--invoice-line-items-action-container">
              <button type="button"
                className="button button-primary parkchirp--action"
                onClick={() => {
                  this.editStateStoredValue.line_items =invoice.fields.line_items.value;
                  this.setState({ invoiceEditState: true })
                }}>
                Adjust Invoice
              </button>

              {/* TO DO: when removing feature flag, remove conditional entirely (button always shows) */}
              {(featureFlags.OVERPAYMENT || (!featureFlags.OVERPAYMENT && invoice.fields.balance.value > 0)) &&
                <button type="button"
                  className="button button-primary parkchirp--action"
                  onClick={this.handleApplyPaymentClick}>
                  Apply Payment
                </button>
              }

              {invoice.reversablePayments?.length > 0 && <button type="button"
                className="button button-primary parkchirp--action"
                onClick={this.handleApplyRefundClick}>
                Refund/Reverse Payment
              </button>}
            </div>
            {featureFlags.REVERSE_APPLICATIONDATE && creditReversal.credit_reversed && creditReversal.overpayment_credit &&
              <div className='parkchirp--invoice-panel--invoice-line-items-action-container'>
                <NoticeBlock id="credit-reversal-success" type="message"
                    containerClasses={['parkchirp--sidebar-block', 'parkchirp--invoice-panel--notice']}>
                  <p>The payment and associated credit {creditReversal.overpayment_credit.readable_id} have been reversed.</p>
                </NoticeBlock>
              </div>
            }
            {!creditReversal.credit_reversed && creditReversal.overpayment_credit &&
              <div className="parkchirp--invoice-panel--invoice-line-items-action-container">
                <NoticeBlock type="message"
                  containerClasses={['parkchirp--sidebar-block', 'parkchirp--invoice-panel--notice']}>
                  <p>
                    The payment has been reversed.
                    The payment resulted in credit {creditReversal.overpayment_credit.readable_id}.
                    Since the credit was created, it was either applied to another invoice or adjusted.
                    Please navigate to the credit screen to make the proper changes to the credit.
                  </p>
                </NoticeBlock>
              </div>
            }
          </>
        }
        {(paymentEditState) &&
          <div className="parkchirp--invoice-panel--payment-panel-container">
            <InvoicePaymentPanel
              invoice={invoice}
              facilityId={this.props.config.facilityId}
              editMode={paymentEditState}
              panelHeading="Apply Payment"
              onSave={this.exitPaymentEditState}
              onCancel={this.exitPaymentEditState}
            />
          </div>
        }
        {refundEditState &&
          <div className="parkchirp--invoice-panel--payment-panel-container">
            <InvoiceRefundPanel invoice={invoice}
              facilityId={this.props.config.facilityId}
              editMode={refundEditState}
              panelHeading="Apply Refund/Reversal"
              onSave={this.exitRefundEditState}
              onCancel={this.exitRefundEditState}
              transactions={transactions}
            />
          </div>
        }
      </React.Fragment>
    );
  }


  renderInvoiceNotes() {
    const {
      invoice,
      invoiceNotesEditState,
      invoiceNotesSaveInProgress,
      invoiceDetailNotesEditState,
      invoiceDetailNotesSaveInProgress,
      invoiceLineItemsEditState,
      invoiceDueDateEditState,
      invoiceDueDateSaveInProgress,
      paymentEditState,
      transactionEditState
    } = this.state;

    const {
      newMode
    } = this.props;

    return (
      <div className="parkchirp--invoice-panel--invoice-notes-container">
        <Field label="General Notes (Internal Use Only)"
          id="parkchirp--invoice--notes"
          containerClasses={['parkchirp--invoice-detail--field-container--notes']}
          layout="stacked">
          <InputTextField inputId="parkchirp--invoice--notes"
            value={invoice.fields.notes.value}
            onChange={this._storeInvoiceData('notes')}
            multiLine={true}
            editMode={newMode || invoiceNotesEditState}
          />
          {(!newMode && !invoiceLineItemsEditState && !paymentEditState) &&
            <div className="parkchirp--invoice-panel--action-container--notes">
              {(invoiceNotesSaveInProgress) &&
                <ProcessingIndicator />
              }
              {(!invoiceDueDateEditState && !invoiceDueDateSaveInProgress && !invoiceNotesEditState && !invoiceNotesSaveInProgress && !transactionEditState) &&
                <button type="button"
                  className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                  onClick={this.handleInvoiceNotesEditClick}>
                  Edit
                </button>
              }
              {(invoiceNotesEditState && !invoiceNotesSaveInProgress) &&
                <React.Fragment>
                  <button type="button"
                    className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                    onClick={this.handleInvoiceNotesSaveClick}>
                    Save
                  </button>
                  <button type="button"
                    className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                    onClick={this.handleInvoiceNotesCancelClick}>
                    Cancel
                  </button>
                </React.Fragment>
              }
            </div>
          }
        </Field>
        <Field label="Invoice Detail Notes"
          id="parkchirp--invoice--detail-notes"
          containerClasses={['parkchirp--invoice-detail--field-container--notes']}
          layout="stacked">
          <p><i>These notes will appear on the customer's invoice.</i></p>
          <InputTextField inputId="parkchirp--invoice--notes"
            value={invoice.fields.detail_notes.value}
            onChange={this._storeInvoiceData('detail_notes')}
            multiLine={true}
            editMode={newMode || invoiceDetailNotesEditState}
          />
          {(!newMode && !invoiceLineItemsEditState && !paymentEditState) &&
            <div className="parkchirp--invoice-panel--action-container--notes">
              {(invoiceDetailNotesSaveInProgress) &&
                <ProcessingIndicator />
              }
              {(!invoiceDueDateEditState && !invoiceDueDateSaveInProgress && !invoiceDetailNotesEditState && !invoiceDetailNotesSaveInProgress && !transactionEditState) &&
                <button type="button"
                  className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                  onClick={this.handleInvoiceDetailNotesEditClick}>
                  Edit
                </button>
              }
              {(invoiceDetailNotesEditState && !invoiceDetailNotesSaveInProgress) &&
                <React.Fragment>
                  <button type="button"
                    className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                    onClick={this.handleInvoiceDetailNotesSaveClick}>
                    Save
                  </button>
                  <button type="button"
                    className="parkchirp--invoice-attribute-action parkchirp--button-as-link"
                    onClick={this.handleInvoiceDetailNotesCancelClick}>
                    Cancel
                  </button>
                </React.Fragment>
              }
            </div>
          }
        </Field>
      </div>
    );
  }

  renderTransactionTable() {
    const {
      invoice,
      invoiceLineItemsEditState,
      invoiceNotesEditState,
      invoiceDueDateEditState,
      paymentEditState,
      refundEditState,
    } = this.state;
    const disabled = invoiceLineItemsEditState || invoiceNotesEditState || invoiceDueDateEditState || refundEditState || paymentEditState;
    return (
      <React.Fragment>
        <div className="parkchirp--invoice-panel--invoice-notes-container">
          <Field label="Invoice History">
            <TransactionTable id="invoice-transactions"
              invoiceId={invoice.fields.id.value}
              onSaveButtonClick={this.handleTransactionNotesSaveClick}
              onCancelButtonClick={this.handleTransactionNotesCancelClick}
              onEditButtonClick={this.handleTransactionNotesEditClick}
              disabled={disabled}
            />
          </Field>
        </div>
      </React.Fragment>
    );
  }


  render() {
    const { newMode } = this.props;
    const { invoice, invoiceEditState } = this.state;
    return (
      <React.Fragment>
        <InvoiceContext.Provider value={{ invoice }}>
          {this.renderInvoiceAttributes()}
          {this.renderInvoiceActions()}
          {invoiceEditState && this.renderAdjustInvoicePanel()}
          {invoice.isCarryover && newMode ? this.renderCarryoverLineItem() : this.renderInvoiceLineItems()}
          {this.renderInvoiceNotes()}
          {!newMode && this.renderTransactionTable()}
        </InvoiceContext.Provider>
      </React.Fragment>
    );
  }
}

export default InvoicePanel;
